/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import type { QueryParams, QueryPrimitive } from '@/utils/types.ts'

function buildUrl(input: string | URL, params?: QueryParams): string {
  const url = new URL(typeof input === 'string' ? input : input.toString(), window.location.origin)

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue

      const add = (v: QueryPrimitive) => {
        if (v instanceof Date) {
          url.searchParams.append(key, v.toISOString())
        } else {
          url.searchParams.append(key, String(v))
        }
      }

      if (Array.isArray(value)) {
        value.forEach(add)
      } else {
        // On remplace plutôt qu’append si c’est un seul
        if (value instanceof Date) {
          url.searchParams.set(key, value.toISOString())
        } else {
          url.searchParams.set(key, String(value))
        }
      }
    }
  }

  return url.toString()
}

let _csrf: string
function getCsrfToken(): string {
  if (!_csrf) {
    _csrf =
      document.cookie
        .split(';')
        .find((c) => c.trim().startsWith('csrftoken='))
        ?.split('=')[1]
        ?.trim() ?? ''
  }

  return _csrf
}

export type HttpResponse<TResponse> = {
  url: string
  status: number
  statusText: string
  headers: Headers
  data: TResponse
}

export class HttpError extends Error {
  constructor(
    public res: Response,
    public body: unknown
  ) {
    super(`HTTP Error while performing request: ${res.url} ${res.status} ${res.statusText}`)
  }
}

export class ValidationError extends HttpError {
  constructor(
    res: Response,
    public problems: Record<string, string[]>
  ) {
    super(res, problems)
  }
}

export async function request<TResponse = unknown>(input: string | URL, init?: RequestInit) {
  const hasBody = init?.body !== undefined && init.body !== null

  const headers = new Headers(init?.headers)
  headers.set('Accept-Language', document.documentElement.lang)

  if (hasBody) {
    const csrf = getCsrfToken()
    if (csrf) headers.set('X-CSRFToken', csrf)

    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(input, {
    ...(init || {}),
    headers: headers,
    redirect: 'follow',
  })

  console.log(`fetch at ${input}`)

  const body: unknown = res.headers.get('content-type') === 'application/json' ? await res.json() : await res.text()

  if (!res.ok) {
    if (res.status === 400 && typeof body === 'object') {
      // Just cast the body for convenience and hope for the best :D
      // It's fine so long we know for sure there will not be another format
      throw new ValidationError(res, body as Record<string, string[]>)
    }

    throw new HttpError(res, body)
  }

  return body as TResponse
}

export function get<TResponse = unknown>(input: string | URL, init?: RequestInit & { query?: QueryParams }) {
  const { query, ...rest } = init || {}
  const url = query ? buildUrl(input, query) : input

  return request<TResponse>(url, {
    ...rest,
    method: 'GET',
  })
}

export function post<TResponse = unknown>(input: string | URL, body?: unknown, init?: RequestInit) {
  return request<TResponse>(input, {
    ...(init || {}),
    method: 'POST',
    body: body !== null ? JSON.stringify(body) : null,
  })
}

export function patch<TResponse = unknown>(input: string | URL, body?: unknown, init?: RequestInit) {
  return request<TResponse>(input, {
    ...(init || {}),
    method: 'PATCH',
    body: body !== null ? JSON.stringify(body) : null,
  })
}

export function del<TResponse = unknown>(input: string | URL, body?: unknown, init?: RequestInit) {
  return request<TResponse>(input, {
    ...(init || {}),
    method: 'DELETE',
    body: body !== null && body !== undefined ? JSON.stringify(body) : null,
  })
}
